<script setup>
import { ref, computed, watch } from 'vue';
import wootConstants from 'dashboard/constants/globals';
import ClassificationBadge from './ClassificationBadge.vue';
import CommentTypeBadge from './CommentTypeBadge.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import PlatformBadge from './PlatformBadge.vue';

const props = defineProps({
  initialState: {
    type: Object,
    default: () => ({}),
  },
  brands: {
    type: Array,
    default: () => [],
  },
  labels: {
    type: Array,
    default: () => [],
  },
  customClassifications: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['filter']);

const cloneFilterState = state => {
  if (!state || typeof state !== 'object') return {};
  try {
    return structuredClone(state);
  } catch {
    return { ...state };
  }
};

const showMenu = ref({
  sort: false,
  filter: false,
});

const activeOption = ref({
  sort: wootConstants.SORT_LABELS.NEWEST,
  filter: wootConstants.FILTER_LABELS.COMMENT_DATE,
});

const filter = ref({});

const classificationOptions = [
  {
    name: wootConstants.INTERPRETATION_TYPES.POSITIVE,
    key: wootConstants.INTERPRETATION_TYPES.POSITIVE,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.NEGATIVE,
    key: wootConstants.INTERPRETATION_TYPES.NEGATIVE,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.NEUTRAL,
    key: wootConstants.INTERPRETATION_TYPES.NEUTRAL,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.IRONY,
    key: wootConstants.INTERPRETATION_TYPES.IRONY,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.SPAM,
    key: wootConstants.INTERPRETATION_TYPES.SPAM,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.UNKNOWN,
    key: wootConstants.INTERPRETATION_TYPES.UNKNOWN,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
];

const respondedOptions = [
  {
    name: wootConstants.INTERPRETATION_TYPES.AI_REPLY,
    key: wootConstants.INTERPRETATION_TYPES.AI_REPLY,
    selected: false,
    type: wootConstants.FILTER_TYPE.REPLY_BY_AI,
  },
];

const sortOptions = [
  {
    name: wootConstants.SORT_LABELS.NEWEST,
    key: wootConstants.INBOX_SORT_BY.NEWEST,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
  {
    name: wootConstants.SORT_LABELS.OLDEST,
    key: wootConstants.INBOX_SORT_BY.OLDEST,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
];

const filterOptions = [
  {
    name: wootConstants.FILTER_LABELS.COMMENT_DATE,
    key: wootConstants.FILTER_KEYS.COMMENT_DATE,
    type: wootConstants.FILTER_TYPES.DATE,
  },
  {
    name: wootConstants.FILTER_LABELS.PUBLICATION_DATE,
    key: wootConstants.FILTER_KEYS.PUBLICATION_DATE,
    type: wootConstants.FILTER_TYPES.DATE,
  },
];

const platformOptions = [
  {
    name: wootConstants.PLATFORM_TYPES.FACEBOOK,
    key: wootConstants.PLATFORM_TYPES.FACEBOOK,
    selected: false,
    type: wootConstants.FILTER_TYPE.PLATFORM,
  },
  {
    name: wootConstants.PLATFORM_TYPES.INSTAGRAM,
    key: wootConstants.PLATFORM_TYPES.INSTAGRAM,
    selected: false,
    type: wootConstants.FILTER_TYPE.PLATFORM,
  },
];

const commentTypeOptions = [
  {
    name: wootConstants.COMMENT_TYPES.FEED,
    key: wootConstants.COMMENT_TYPES.FEED,
    selected: false,
    type: wootConstants.FILTER_TYPE.ORIGIN,
  },
  {
    name: wootConstants.COMMENT_TYPES.REELS,
    key: wootConstants.COMMENT_TYPES.REELS,
    selected: false,
    type: wootConstants.FILTER_TYPE.ORIGIN,
  },
  {
    name: wootConstants.COMMENT_TYPES.AD,
    key: wootConstants.COMMENT_TYPES.AD,
    selected: false,
    type: wootConstants.FILTER_TYPE.ORIGIN,
  },
];

const activeFilterOption = computed(() => {
  return filterOptions.find(
    option => option.name === activeOption.value.filter
  );
});

const getBrands = computed(() => {
  const formattedBrands = [];
  props.brands.forEach(brand => {
    formattedBrands.push({
      name: brand.name,
      key: brand.id,
      selected: false,
      type: 'brand',
    });
  });
  return formattedBrands.sort((a, b) => a.name.localeCompare(b.name));
});

const getLabels = computed(() => {
  const formattedLabels = [];
  props.labels.forEach(label => {
    formattedLabels.push({
      name: label.title,
      key: label.title,
      color: label.color,
      selected: false,
      type: 'label',
    });
  });
  return formattedLabels.sort((a, b) => a.name.localeCompare(b.name));
});

const getCustomClassifications = computed(() => {
  const formattedClassifications = [];
  props.customClassifications.forEach(classification => {
    formattedClassifications.push({
      name: classification,
      key: classification,
      selected: false,
      type: 'customClassifications',
    });
  });
  return formattedClassifications.sort((a, b) => a.name.localeCompare(b.name));
});

const addOrRemoveFromStack = (stack, value, prop) => {
  if (!stack[prop]) {
    stack[prop] = [];
  }
  if (stack[prop].find(e => e.key === value.key)) {
    stack[prop] = stack[prop].filter(item => item.key !== value.key);
  } else {
    stack[prop].push(value);
  }
};

const isChecked = (value, prop) => {
  return filter.value[prop]?.find(item => item.key === value.key);
};

const onFilterChange = (value, prop) => {
  if (
    [
      'brand',
      'platform',
      'origin',
      'classification',
      'purchaseIntention',
      'reply_by_ai',
      'label',
      'customClassifications',
    ].includes(prop)
  ) {
    addOrRemoveFromStack(filter.value, value, prop);
  } else {
    filter.value[prop] = value;
  }
};

watch(
  () => props.initialState,
  newState => {
    filter.value = cloneFilterState(newState);
    if (!filter.value.filter) {
      filter.value.filter = filterOptions[0];
    }
    if (!filter.value.sort) {
      filter.value.sort = sortOptions[0];
    }
    activeOption.value = {
      sort: filter.value.sort.name,
      filter: filter.value.filter.name,
    };
  },
  { immediate: true }
);

const applyFilters = () => {
  emit('filter', cloneFilterState(filter.value));
};

const onMenuOpen = menu => {
  showMenu.value[menu] = !showMenu.value[menu];
};

const onMenuOptionClick = (option, menu) => {
  activeOption.value[menu] = option.name;
  showMenu.value[menu] = false;
  onFilterChange(option, menu);
};
</script>

<template>
  <div
    class="flex flex-col bg-white dark:bg-slate-800 w-full sm:w-[560px] md:w-[610px] border shadow-lg border-slate-200 dark:border-slate-700/50 rounded-xl overflow-hidden"
  >
    <!-- Sort Section -->
    <div
      class="flex flex-col gap-2 p-4 border-b border-slate-100 dark:border-slate-700/50"
    >
      <label
        class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
      >
        {{ $t('COMMENTS.FILTER.SORT_BY') }}
      </label>
      <div class="relative">
        <button
          type="button"
          class="w-full h-10 flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          @click="onMenuOpen('sort')"
        >
          <span>{{ activeOption.sort }}</span>
          <fluent-icon
            icon="chevron-down"
            size="16"
            class="text-slate-500 dark:text-slate-400 transition-transform"
            :class="{ 'rotate-180': showMenu.sort }"
          />
        </button>
        <div
          v-if="showMenu.sort"
          class="absolute top-full mt-1 w-full bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden"
        >
          <button
            v-for="option in sortOptions"
            :key="option.key"
            type="button"
            class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
            :class="{
              'bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400':
                activeOption.sort === option.name,
              'text-slate-700 dark:text-slate-300':
                activeOption.sort !== option.name,
            }"
            @click.stop="onMenuOptionClick(option, 'sort')"
          >
            <span>{{ option.name }}</span>
            <fluent-icon
              v-if="activeOption.sort === option.name"
              icon="checkmark"
              size="16"
              class="text-woot-600 dark:text-woot-400"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Filter Type Section -->
    <div
      class="flex flex-col gap-2 p-4 border-b border-slate-100 dark:border-slate-700/50"
    >
      <label
        class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
      >
        {{ $t('COMMENTS.FILTER.FILTER_BY') }}
      </label>
      <div class="relative">
        <button
          type="button"
          class="w-full h-10 flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          @click="onMenuOpen('filter')"
        >
          <span>{{ activeOption.filter }}</span>
          <fluent-icon
            icon="chevron-down"
            size="16"
            class="text-slate-500 dark:text-slate-400 transition-transform"
            :class="{ 'rotate-180': showMenu.filter }"
          />
        </button>
        <div
          v-if="showMenu.filter"
          class="absolute top-full mt-1 w-full bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden"
        >
          <button
            v-for="option in filterOptions"
            :key="option.key"
            type="button"
            class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
            :class="{
              'bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400':
                activeOption.filter === option.name,
              'text-slate-700 dark:text-slate-300':
                activeOption.filter !== option.name,
            }"
            @click.stop="onMenuOptionClick(option, 'filter')"
          >
            <span>{{ option.name }}</span>
            <fluent-icon
              v-if="activeOption.filter === option.name"
              icon="checkmark"
              size="16"
              class="text-woot-600 dark:text-woot-400"
            />
          </button>
        </div>
      </div>

      <!-- Date Range Picker -->
      <div
        v-if="activeFilterOption?.type === 'date'"
        class="flex flex-col sm:flex-row gap-2 mt-2 text-sm"
      >
        <div class="flex items-center gap-2 flex-1">
          <span
            class="text-slate-600 dark:text-slate-400 whitespace-nowrap text-xs"
          >
            {{ $t('COMMENTS.FILTER.FROM') }}
          </span>
          <VueDatePicker
            :model-value="filter.datetimeStart"
            class="flex-1"
            auto-apply
            date-picker
            :enable-time-picker="false"
            locale="pt-BR"
            format="dd/MM/yyyy"
            @update:model-value="e => onFilterChange(e, 'datetimeStart')"
          />
        </div>
        <div class="flex items-center gap-2 flex-1">
          <span
            class="text-slate-600 dark:text-slate-400 whitespace-nowrap text-xs"
          >
            {{ $t('COMMENTS.FILTER.TO') }}
          </span>
          <VueDatePicker
            :model-value="filter.datetimeEnd"
            class="flex-1"
            auto-apply
            date-picker
            :enable-time-picker="false"
            locale="pt-BR"
            format="dd/MM/yyyy"
            @update:model-value="e => onFilterChange(e, 'datetimeEnd')"
          />
        </div>
      </div>
    </div>

    <!-- Filters Content -->
    <div class="flex flex-col gap-5 p-4 max-h-[400px] overflow-y-auto">
      <!-- Brand Filter -->
      <div v-if="getBrands.length" class="flex flex-col gap-2">
        <h3
          class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
        >
          {{ $t('COMMENTS.FILTER.BRAND') }}
        </h3>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          <label
            v-for="option in getBrands"
            :key="option.key"
            class="flex items-center gap-2 cursor-pointer group"
          >
            <input
              :id="option.key"
              type="checkbox"
              :name="option.key"
              :checked="isChecked(option, 'brand')"
              class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
              @change="onFilterChange(option, 'brand')"
            />
            <span
              class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate group-hover:text-woot-600 dark:group-hover:text-woot-400 transition-colors"
            >
              {{ option.name }}
            </span>
          </label>
        </div>
      </div>

      <!-- Label Filter -->
      <div v-if="getLabels.length" class="flex flex-col gap-2">
        <h3
          class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
        >
          {{ $t('COMMENTS.FILTER.LABEL') }}
        </h3>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          <label
            v-for="option in getLabels"
            :key="option.key"
            class="flex items-center gap-2 cursor-pointer group"
          >
            <input
              :id="`label-${option.key}`"
              type="checkbox"
              :name="option.key"
              :checked="isChecked(option, 'label')"
              class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
              @change="onFilterChange(option, 'label')"
            />
            <span
              class="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 truncate group-hover:text-woot-600 dark:group-hover:text-woot-400 transition-colors"
            >
              <span
                class="w-2 h-2 rounded-full flex-shrink-0"
                :style="{ backgroundColor: option.color || '#CBD5F5' }"
              />
              {{ option.name }}
            </span>
          </label>
        </div>
      </div>

      <!-- Platform, Origin, and Replied By - Side by Side on Desktop -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Platform Filter -->
        <div class="flex flex-col gap-2">
          <h3
            class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
          >
            {{ $t('COMMENTS.FILTER.PLATFORM') }}
          </h3>
          <div class="flex flex-col gap-2">
            <label
              v-for="option in platformOptions"
              :key="option.key"
              class="flex items-center gap-2 cursor-pointer group"
            >
              <input
                :id="option.key"
                type="checkbox"
                :name="option.key"
                :checked="isChecked(option, option.type)"
                class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
                @change="onFilterChange(option, option.type)"
              />
              <PlatformBadge :type="option.name" />
            </label>
          </div>
        </div>

        <!-- Origin Filter -->
        <div class="flex flex-col gap-2">
          <h3
            class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
          >
            {{ $t('COMMENTS.FILTER.ORIGIN') }}
          </h3>
          <div class="flex flex-col gap-2">
            <label
              v-for="option in commentTypeOptions"
              :key="option.key"
              class="flex items-center gap-2 cursor-pointer group"
            >
              <input
                :id="option.key"
                type="checkbox"
                :name="option.key"
                :checked="isChecked(option, option.type)"
                class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
                @change="onFilterChange(option, option.type)"
              />
              <CommentTypeBadge :type="option.name" />
            </label>
          </div>
        </div>

        <!-- Replied By Filter -->
        <div class="flex flex-col gap-2">
          <h3
            class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
          >
            {{ $t('COMMENTS.FILTER.REPLIED_BY') }}
          </h3>
          <div class="flex flex-col gap-2">
            <label
              v-for="option in respondedOptions"
              :key="option.key"
              class="flex items-center gap-2 cursor-pointer group"
            >
              <input
                :id="option.key"
                type="checkbox"
                :name="option.key"
                :checked="isChecked(option, option.type)"
                class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
                @change="onFilterChange(option, option.type)"
              />
              <ClassificationBadge :type="option.name" />
            </label>
          </div>
        </div>
      </div>

      <!-- Interpretation Filter -->
      <div class="flex flex-col gap-2">
        <h3
          class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
        >
          {{ $t('COMMENTS.FILTER.INTERPRETATION') }}
        </h3>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label
            v-for="option in classificationOptions"
            :key="option.key"
            class="flex items-center gap-2 cursor-pointer group"
          >
            <input
              :id="option.key"
              type="checkbox"
              :name="option.key"
              :checked="isChecked(option, option.type)"
              class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
              @change="onFilterChange(option, option.type)"
            />
            <ClassificationBadge :type="option.name" />
          </label>
        </div>
      </div>

      <!-- Custom Classifications Filter -->
      <div v-if="getCustomClassifications.length" class="flex flex-col gap-2">
        <h3
          class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
        >
          {{ $t('COMMENTS.FILTER.CUSTOM_CLASSIFICATIONS') }}
        </h3>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label
            v-for="option in getCustomClassifications"
            :key="option.key"
            class="flex items-center gap-2 cursor-pointer group"
          >
            <input
              :id="`custom-${option.key}`"
              type="checkbox"
              :name="option.key"
              :checked="isChecked(option, 'customClassifications')"
              class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
              @change="onFilterChange(option, 'customClassifications')"
            />
            <span
              class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate group-hover:text-woot-600 dark:group-hover:text-woot-400 transition-colors"
            >
              {{ option.name }}
            </span>
          </label>
        </div>
      </div>
    </div>

    <div
      class="p-4 border-t border-slate-100 dark:border-slate-700/50 bg-slate-50/80 dark:bg-slate-900/50"
    >
      <button
        type="button"
        class="w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-woot-500 hover:bg-woot-600 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors"
        @click="applyFilters"
      >
        {{ $t('COMMENTS.MODERATION.APPLY_FILTERS') }}
      </button>
    </div>
  </div>
</template>
